import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projects, getStatusColor } from '../../data/projects';
import styles from './ProjectPage.module.css';

const typeIcon = {
  finding:   '🔍',
  data:      '📊',
  method:    '⚗️',
  correction:'🔧',
  milestone: '🏁',
};

const ProjectPage = () => {
  const { slug } = useParams();
  const project = projects.find(p => p.slug === slug);
  const [followed, setFollowed] = useState(false);

  if (!project) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.notFound}>
            <p>Project not found.</p>
            <Link to="/portfolio" className={styles.backLink}>← Back to Portfolio</Link>
          </div>
        </div>
      </main>
    );
  }

  const statusColors = getStatusColor(project.status);

  return (
    <main className={styles.main}>
      {/* ── Gradient header band ── */}
      <div className={styles.heroBand} style={{ background: project.gradient }} aria-hidden="true" />

      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div className={styles.breadcrumb}>
          <Link to="/portfolio" className={styles.breadcrumbLink}>Portfolio</Link>
          <span className={styles.breadcrumbSep}>›</span>
          <span className={styles.breadcrumbCurrent}>{project.title}</span>
        </div>

        {/* ── Project header ── */}
        <header className={styles.projectHeader}>
          <div className={styles.headerTop}>
            <span className={styles.categoryTag}>{project.category}</span>
            <span
              className={styles.statusBadge}
              style={{ background: statusColors.bg, color: statusColors.text }}
            >
              <span
                className={`${styles.statusDot} ${project.status === 'Live' ? styles.statusDotPulse : ''}`}
                style={{ background: statusColors.dot }}
              />
              {project.status}
            </span>
          </div>

          <h1 className={styles.projectTitle}>{project.title}</h1>

          <div className={styles.authorRow}>
            <div
              className={styles.authorAvatar}
              style={{ background: `${project.author.color}22`, color: project.author.color }}
            >
              {project.author.initials}
            </div>
            <div className={styles.authorInfo}>
              <span className={styles.authorName}>{project.author.name}</span>
              <span className={styles.authorMeta}>
                {project.author.university} · {project.author.country}
              </span>
            </div>
            <div className={styles.projectMeta}>
              <span className={styles.metaItem}>Started {project.startDate}</span>
              <span className={styles.metaDot}>·</span>
              <span className={styles.metaItem}>Updated {project.lastUpdate}</span>
            </div>
          </div>

          <div className={styles.statsRow}>
            <div className={styles.statPill}>
              <svg viewBox="0 0 16 16" fill="none" className={styles.statIcon}>
                <path d="M8 2C4.5 2 1.5 4.5 1.5 8c0 1.5.5 2.9 1.4 4L2 14l2.1-.8C5.3 14 6.6 14.5 8 14.5c3.5 0 6.5-2.5 6.5-6S11.5 2 8 2z" stroke="currentColor" strokeWidth="1.3"/>
              </svg>
              {project.followers.toLocaleString()} followers
            </div>
            <div className={styles.statPill}>
              <svg viewBox="0 0 16 16" fill="none" className={styles.statIcon}>
                <path d="M8 1.5v4M8 10.5V13M3 8H1M15 8h-2M4.5 4.5L3 3M13 13l-1.5-1.5M4.5 11.5L3 13M13 3l-1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              {project.updates} updates
            </div>
            <button
              className={`${styles.followBtn} ${followed ? styles.followBtnActive : ''}`}
              onClick={() => setFollowed(f => !f)}
            >
              {followed ? '✓ Following' : '+ Follow this project'}
            </button>
          </div>

          <div className={styles.tags}>
            {project.tags.map(tag => (
              <span key={tag} className={styles.tag}>{tag}</span>
            ))}
          </div>
        </header>

        {/* ── Two-column layout ── */}
        <div className={styles.layout}>

          {/* ── Main column ── */}
          <div className={styles.mainCol}>

            {/* Full description */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>About this project</h2>
              <p className={styles.fullDescription}>{project.fullDescription}</p>
            </section>

            {/* Timeline */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Research journal</h2>
              <div className={styles.timeline}>
                {project.timeline.map((entry, i) => (
                  <div key={i} className={styles.timelineEntry}>
                    <div className={styles.timelineDot}>
                      <span className={styles.timelineIcon}>{typeIcon[entry.type] || '📌'}</span>
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineHeader}>
                        <span className={styles.timelineTitle}>{entry.title}</span>
                        <span className={styles.timelineDate}>{entry.date}</span>
                      </div>
                      <p className={styles.timelineText}>{entry.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Comments */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Community discussion</h2>
              <div className={styles.comments}>
                {project.comments.map((comment, i) => (
                  <div key={i} className={styles.comment}>
                    <div
                      className={styles.commentAvatar}
                      style={{ background: `${comment.color}20`, color: comment.color }}
                    >
                      {comment.initials}
                    </div>
                    <div className={styles.commentBody}>
                      <div className={styles.commentHeader}>
                        <span className={styles.commentAuthor}>{comment.author}</span>
                        <span className={styles.commentAffil}>{comment.affiliation}</span>
                        <span className={styles.commentDate}>{comment.date}</span>
                      </div>
                      <p className={styles.commentText}>{comment.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

          </div>

          {/* ── Sidebar ── */}
          <aside className={styles.sidebar}>
            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>Project details</div>
              <dl className={styles.detailList}>
                <dt>Field</dt>
                <dd>{project.category}</dd>
                <dt>Status</dt>
                <dd>
                  <span style={{ color: statusColors.text }}>{project.status}</span>
                </dd>
                <dt>Started</dt>
                <dd>{project.startDate}</dd>
                <dt>Last update</dt>
                <dd>{project.lastUpdate}</dd>
                <dt>Location</dt>
                <dd>{project.author.country}</dd>
              </dl>
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>Latest update</div>
              <p className={styles.recentUpdate}>"{project.recentUpdate}"</p>
            </div>
          </aside>

        </div>

        {/* ── Back button ── */}
        <div className={styles.backRow}>
          <Link to="/portfolio" className={styles.backBtn}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Portfolio
          </Link>
        </div>

      </div>
    </main>
  );
};

export default ProjectPage;
